/**
 * MessageThread Component
 * Displays messages in a channel with support for threads, reactions, and real-time updates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useSwipeActions } from './swipe/useSwipeActions';
import { SENSITIVITY_RATIO, useSwipeSettings } from './swipe/swipeSettings';
import {
  Send,
  Smile,
  Paperclip,
  MoreVertical,
  Reply,
  Pin,
  Pencil,
  Trash2,
  Hash,
  AtSign,
  X,
  MessageSquare,
  FileText,
  Image,
  Film,
  Music,
  Archive,
  FolderOpen,
  Link as LinkIcon,
  AlertTriangle,
  Flag,
  CheckSquare,
  Mail,
  MailOpen,
  UserPlus,
  ClipboardList,
  Calendar,
  ArrowUpCircle,
  CalendarClock,
  BookOpen,
  ShieldCheck,
  Zap,
  Users,
  UserMinus,
  Settings,
  Loader2,
  LucideIcon,
  ArchiveRestore,
  LogOut,
  FilePlus2,
  PlayCircle,
  CheckCircle2,
  RotateCcw,
  PinOff,
  Bot,
} from 'lucide-react';
import {
  messagingApi,
  Message,
  Channel,
  ChannelDetail,
  SendMessageRequest,
  MessagePriority,
  ActionStatus,
  CreateTaskFromMessageRequest,
  MessageActionTemplate,
  UploadedAttachment,
  type ReplyChannel,
} from '@/services/messagingApi';
import { userManagementAPI } from '@/services/api/userManagement';
import { AttachmentDisplay } from './AttachmentActions';
import { MediaLibrarySelector } from '../communications/MediaLibrarySelector';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { InlineNotice, useInlineNotice } from './InlineNotice';
import { toast } from '../ui/use-toast';
import { cn } from '@/lib/utils';
import { getCurrentUserId } from '../../utils/auth';
import { MessageText, firstLinkUrl, visibleMessageText } from './linkify';
import { LinkPreviewCard, MessageLinkPreviews } from './LinkPreviewCard';
import {
  DeleteChannelDialog,
  getChannelPermissions,
  useChannelLifecycle,
  useIsOrgAdmin,
} from './channelActions';
import { useChannelPins, useIsChannelPinned } from './channelPins';
import { DocumentPickerDialog, SelectedDocument } from './DocumentPickerDialog';
import { AttachMenu } from './AttachMenu';
import { InsertLinkControl, useComposerLink } from './InsertLink';
import { ProviderMark } from './cloud/CloudIcons';
import { CreateTaskDialog, openTaskFromMessaging } from './CreateTaskDialog';
import { useSupportBubbleClearance } from '@/hooks/useSupportBubbleClearance';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { ReactionBadges, REACTION_ROW_CLEARANCE_CLASS } from './ReactionBadges';
import { ScheduleSendMenu } from './ScheduleSendMenu';
import { ScheduledMessagesChip, scheduledMessagesKey } from './ScheduledMessagesPanel';
import { SeenStatus, newestMessageId, useMarkChannelRead } from './ReadReceipts';
import { formatScheduleTime } from './scheduleSend';
import { MentionPicker, useMentionComposer } from './mentions';
import { callMetaOf, callNotesMetaOf, type CallMessageMeta } from '@/services/callsApi';
import { CallHeaderButtons } from './calls/CallHeaderButtons';
import { CallMessageCard } from './calls/CallMessageCard';
import { CallNotesCard } from './calls/CallNotesCard';
import { CallNotesButton } from './calls/CallNotesPanel';
// TRUS-691: scheduled meetings (card, schedule dialog, upcoming list).
import { meetingMetaOf } from '@/services/meetingsApi';
import { MeetingMessageCard } from './meetings/MeetingMessageCard';
import { useMeetingScheduler } from './meetings/useMeetingScheduler';
import { ConversationMeetingsSection } from './meetings/UpcomingMeetings';
import { systemEventMetaOf } from '@/services/systemEventsApi';
import { SystemEventCard, SystemEventSender } from './systemEvents/SystemEventCard';
import { ChannelEventsDialog } from './systemEvents/ChannelEventsDialog';
import { ReplyChannelPicker } from './ReplyChannelPicker';
import { DeliveryStatusBadge, MessageChannelBadge } from './MessageChannelBadge';
import { MemberDmCardView, MirroredSourceCaption } from './MemberDmCardView';
import { cardCaptionSourceOf, memberDmCardOf, mirroredSourceOf } from './memberDmCards';
// TRUS-719: "Maria is typing…" in member DMs.
import { useTypingIndicator } from './typing/useTypingIndicator';
import { TypingIndicator } from './typing/TypingIndicator';
import { staffTypingApi } from '@/services/inboxPhase4Api';
// TRUS-709: owners / responders header, staff sender names, the "someone else replied" banner.
import { ConversationOwnersBar } from './inbox/ConversationOwnersBar';
import { StaffReplyBanner } from './inbox/StaffReplyBanner';
import { resolveStaffSenderName, showStaffName } from './inbox/memberInbox';
import {
  blockReasonText,
  deliveryBlockedOf,
  effectiveReplyChannel,
  isInternalNote,
  normalizeChannel,
  type DeliveryBlocked,
} from './replyChannel';

// Absolute, locally-aligned message timestamp. channel_messages.created_at is a
// `timestamp without time zone` (UTC wall-clock, serialized with no offset), so we
// append 'Z' when no timezone marker is present before converting to the viewer's
// local zone — otherwise new Date() would mis-read UTC as local.
function formatLocalDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return '';
  return format(d, 'MMM d, yyyy · h:mm a');
}

// Priority display configuration
const PRIORITY_CONFIG = {
  normal: { label: 'Normal', color: 'text-gray-500', bgColor: 'bg-gray-100', icon: null },
  high: { label: 'High', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: Flag },
  urgent: { label: 'Urgent', color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle },
};

// Map action template icon names to Lucide components
const ACTION_ICON_MAP: Record<string, LucideIcon> = {
  'clipboard-list': ClipboardList,
  'arrow-up-circle': ArrowUpCircle,
  'calendar-clock': CalendarClock,
  'flag': Flag,
  'book-open': BookOpen,
  'shield-check': ShieldCheck,
  'zap': Zap,
};

function getActionIcon(iconName: string | null): LucideIcon {
  if (!iconName) return Zap;
  return ACTION_ICON_MAP[iconName] || Zap;
}

// Interface for media library items
interface MediaAsset {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  asset_type: string;
  mime_type?: string;
  file_size?: number;
  external_url?: string;
  thumbnail_url?: string;
  short_url?: string;
  is_public: boolean;
}

interface MessageThreadProps {
  channel: Channel | ChannelDetail;
  onOpenThread?: (message: Message) => void;
  /** Open the "add people" picker for this channel (handled by the workspace). */
  onAddPeople?: () => void;
  /** The channel was deleted, archived or left — the parent should deselect it. */
  onChannelRemoved?: () => void;
  /** Offer "Mark as urgent" (false for member-portal contexts). Default true. */
  allowUrgent?: boolean;
  /** Offer members in the @ picker (false for member-portal contexts). Default true. */
  allowMemberMentions?: boolean;
}

const COMMON_REACTIONS = ['👍', '❤️', '😂', '🎉', '🤔', '👀'];
/** TRUS-759: how long a touch must rest on a message to open its actions. */
const LONG_PRESS_MS = 450;

export function MessageThread({
  channel,
  onOpenThread,
  onAddPeople,
  onChannelRemoved,
  allowUrgent = true,
  allowMemberMentions = true,
}: MessageThreadProps) {
  const { t, language } = useAppTranslation();
  const [messageInput, setMessageInput] = useState('');
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState<number | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [selectedMediaAssets, setSelectedMediaAssets] = useState<MediaAsset[]>([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<MessagePriority>('normal');
  const [isActionRequired, setIsActionRequired] = useState(false);
  const [actionDueDate, setActionDueDate] = useState<string>('');
  const [actionDescription, setActionDescription] = useState<string>('');
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<SelectedDocument[]>([]);
  // Google Drive / OneDrive / SharePoint files — already attachment rows
  // (attach-cloud-file runs on pick), so only their ids are sent.
  const [cloudAttachments, setCloudAttachments] = useState<UploadedAttachment[]>([]);
  const [taskDialogMessage, setTaskDialogMessage] = useState<Message | null>(null);
  // Composer link preview: the first URL in the draft (debounced) and whether
  // the sender dismissed it — a dismissal suppresses previews on the sent message.
  const [draftPreviewUrl, setDraftPreviewUrl] = useState<string | null>(null);
  const [previewsDismissed, setPreviewsDismissed] = useState(false);
  const messagesListRef = useRef<HTMLDivElement>(null);
  // Whether the list should stay pinned to the newest message when it resizes
  // (the composer growing / shrinking under it). Cleared once the user scrolls up.
  const stickToBottomRef = useRef(true);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerLink = useComposerLink(messageInputRef, messageInput, setMessageInput);
  const queryClient = useQueryClient();
  // Feedback: success confirmations are transient toasts; failures stay in an
  // inline strip above the thread until dismissed (dialog errors render inside
  // their dialog instead).
  const { notice, show: showNotice, clear: clearNotice } = useInlineNotice();
  const notify = ({ title, description, variant }: { title: string; description?: string; variant?: 'destructive' }) => {
    if (variant === 'destructive') showNotice({ kind: 'error', title, description });
    else toast({ title, description });
  };

  // Get organization ID for fetching users
  const cboId = localStorage.getItem('active_cbo_id');

  // Find member_id from channel members (for task creation)
  // Check if channel has members array (ChannelDetail) and find first member with member_id
  const channelMemberId = 'members' in channel
    ? channel.members.find(m => m.member_id !== null)?.member_id
    : null;

  // Check if the current user can post in this channel
  const isReadonly = channel.is_readonly;
  const currentUserCanPost = 'members' in channel
    ? channel.members.some(m => m.can_post)
    : true; // Default to true if we don't have detailed member info yet
  const canCompose = !isReadonly && currentUserCanPost;

  // @ / # picker. Members are offered to staff composers only: a member-portal
  // participant (a channel member row carrying member_id) never sees them.
  const viewerIsMember =
    'members' in channel &&
    channel.members.some((m) => m.user_id === getCurrentUserId() && m.member_id != null);
  // TRUS-702: in a member DM, staff pick where a reply goes (Portal / SMS /
  // Email / internal Note). No reply-options (older backend, or not a member
  // DM) → no picker, and sends go out exactly as before.
  const isMemberDm = channel.dm_member_id != null;
  const { data: replyOptions = null } = useQuery({
    queryKey: ['messaging-reply-options', channel.id],
    queryFn: () => messagingApi.getReplyOptions(channel.id),
    enabled: isMemberDm && !viewerIsMember,
    retry: false,
    staleTime: 30_000,
  });
  const [pickedReplyChannel, setPickedReplyChannel] = useState<ReplyChannel | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [deliveryBlocked, setDeliveryBlocked] = useState<DeliveryBlocked | null>(null);
  const replyChannel = isMemberDm && !viewerIsMember ? effectiveReplyChannel(replyOptions, pickedReplyChannel) : null;
  const noteMode = replyChannel === 'note';
  useEffect(() => {
    setPickedReplyChannel(null);
    setEmailSubject('');
    setDeliveryBlocked(null);
  }, [channel.id]);

  // TRUS-719: typing indicator, staff side of a member DM. Polls who is typing
  // every 3 s while the thread is visible; pings (≤ every 4 s) while the
  // composer has text and focus — never for an internal note, which the
  // member will not receive. Hidden for the session on a 404 / 501.
  const [composerFocused, setComposerFocused] = useState(false);
  const typingChannelId = channel.id;
  const typingIndicator = useTypingIndicator({
    surface: 'staff',
    threadKey: typingChannelId,
    enabled: isMemberDm && !viewerIsMember,
    transport: {
      get: () => staffTypingApi.get(typingChannelId),
      ping: () => staffTypingApi.ping(typingChannelId),
    },
  });
  const notifyTyping = typingIndicator.notifyComposer;
  useEffect(() => {
    if (!noteMode) notifyTyping(messageInput, composerFocused);
  }, [messageInput, composerFocused, noteMode, notifyTyping]);

  const mentions = useMentionComposer({
    inputRef: messageInputRef,
    setValue: setMessageInput,
    channelId: channel.id,
    includeMembers: allowMemberMentions && !viewerIsMember,
  });

  // Org staff (task assignees; also the @ picker's fallback list).
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['org-users-mentions', cboId],
    queryFn: async () => {
      if (!cboId) return [];
      return userManagementAPI.listUsers(parseInt(cboId, 10));
    },
    enabled: !!cboId,
  });


  // Fetch messages - include threads to show all messages in conversation
  const {
    data: rawMessages = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['channel-messages', channel.id],
    queryFn: () => messagingApi.getMessages(channel.id, { include_threads: true }),
    refetchInterval: 5000, // Poll for new messages
  });

  // The messages-fetch endpoint reconciles unread_count on the backend (resets it
  // on open). Refresh the sidebar + DM-management lists AFTER that fetch completes —
  // invalidating on channel.id change alone raced ahead of the server reset, so the
  // badge never cleared. Fire once per channel open.
  const lastReadRefreshRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isLoading && lastReadRefreshRef.current !== channel.id) {
      lastReadRefreshRef.current = channel.id;
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
    }
  }, [channel.id, isLoading, rawMessages, queryClient]);

  // Separate pinned messages and sort regular messages by date
  const pinnedMessages = rawMessages.filter(m => m.is_pinned);
  const regularMessages = rawMessages.filter(m => !m.is_pinned);
  // Sort messages chronologically (oldest first) so newest messages appear at bottom
  const messages = [...regularMessages].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Clear the composer after a send or a schedule.
  const resetComposer = () => {
    setMessageInput('');
    setReplyingTo(null);
    setSelectedAttachments([]);
    setSelectedPriority('normal'); // Reset priority after sending
    setIsActionRequired(false); // Reset action required
    setActionDueDate(''); // Reset action due date
    setActionDescription(''); // Reset action description
    setSelectedDocuments([]);
    setCloudAttachments([]);
    setPreviewsDismissed(false);
    setDraftPreviewUrl(null);
  };

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: (request: SendMessageRequest) => messagingApi.sendMessage(request),
    onSuccess: () => {
      resetComposer();
      setSelectedMediaAssets([]);
      setEmailSubject('');
      setDeliveryBlocked(null);
      refetch();
      scrollToBottom();
    },
    onError: (error: Error) => {
      // TRUS-702: consent / contact check failed. Nothing was created; keep the
      // whole draft and say why right above the composer.
      const blocked = deliveryBlockedOf(error);
      if (blocked) {
        setDeliveryBlocked(blocked);
        queryClient.invalidateQueries({ queryKey: ['messaging-reply-options', channel.id] });
        return;
      }
      notify({
        variant: 'destructive',
        title: t('messaging.composer.sendFailed', 'Message not sent'),
        description: error?.message,
      });
    },
  });

  // Schedule send: the same body plus scheduled_at. Nothing lands in the
  // thread until the server sends it; the "Scheduled (N)" chip shows it.
  const scheduleMutation = useMutation({
    mutationFn: (request: SendMessageRequest & { scheduled_at: string; timezone?: string }) =>
      messagingApi.scheduleMessage(request),
    onSuccess: (result, request) => {
      resetComposer();
      setSelectedMediaAssets([]);
      queryClient.invalidateQueries({ queryKey: scheduledMessagesKey(channel.id) });
      const at = new Date(result?.scheduled_at || request.scheduled_at);
      toast({
        title: t('messaging.schedule.scheduledFor', 'Scheduled for {{when}}', {
          when: formatScheduleTime(isNaN(at.getTime()) ? new Date(request.scheduled_at) : at, language),
        }),
      });
    },
    onError: (error: Error) => {
      notify({
        variant: 'destructive',
        title: t('messaging.schedule.failed', 'Message not scheduled'),
        description: error?.message,
      });
    },
  });

  // Edit message mutation
  const editMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      messagingApi.editMessage(id, content),
    onSuccess: () => {
      setEditingMessage(null);
      setMessageInput('');
      refetch();
    },
  });

  // Delete message mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => messagingApi.deleteMessage(id),
    onSuccess: () => refetch(),
  });

  // Reaction mutations
  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: number; emoji: string }) =>
      messagingApi.addReaction(messageId, emoji),
    onSuccess: () => refetch(),
  });

  const removeReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: number; emoji: string }) =>
      messagingApi.removeReaction(messageId, emoji),
    onSuccess: () => refetch(),
  });

  // Pin mutations
  const pinMutation = useMutation({
    mutationFn: (messageId: number) => messagingApi.pinMessage(messageId),
    onSuccess: () => refetch(),
  });

  const unpinMutation = useMutation({
    mutationFn: (messageId: number) => messagingApi.unpinMessage(messageId),
    onSuccess: () => refetch(),
  });

  // Priority mutation
  const setPriorityMutation = useMutation({
    mutationFn: ({ messageId, priority }: { messageId: number; priority: MessagePriority }) =>
      messagingApi.setMessagePriority(messageId, priority),
    onSuccess: () => refetch(),
  });

  // Read/Unread mutations
  const markReadMutation = useMutation({
    mutationFn: (messageId: number) => messagingApi.markMessageRead(messageId),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: (messageId: number) => messagingApi.markMessageUnread(messageId),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
    },
  });

  // Rename channel mutation — wires the existing PATCH /channels/{id} endpoint
  // (owner/admin gated server-side) to update the channel's display name.
  const renameChannelMutation = useMutation({
    mutationFn: (displayName: string) =>
      messagingApi.updateChannel(channel.id, { display_name: displayName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-detail', channel.id] });
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
      setShowRenameDialog(false);
      notify({ title: t('messaging.channel.renamed', 'Channel renamed') });
    },
    onError: (error: Error) => {
      // Shown inside the rename dialog (renameChannelMutation.error).
    },
  });

  // Remove-member mutation — wires the existing DELETE
  // /channels/{id}/members/{channel_members.id} endpoint (owner/admin/self gated
  // server-side) so group owners/admins can remove people from a channel.
  const removeMemberMutation = useMutation({
    mutationFn: (channelMemberId: number) =>
      messagingApi.removeMember(channel.id, channelMemberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-detail', channel.id] });
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
      notify({ title: t('messaging.channel.memberRemoved', 'Member removed') });
    },
    onError: (error: Error) => {
      // Shown inside the members dialog (removeMemberMutation.error).
    },
  });

  // Create task mutation — driven by CreateTaskDialog. Errors show inside the
  // dialog; success shows a toast with an "Open task" action.
  const createTaskMutation = useMutation({
    mutationFn: ({ messageId, request }: { messageId: number; request: CreateTaskFromMessageRequest }) =>
      messagingApi.createTaskFromMessage(messageId, request),
    onSuccess: (result) => {
      refetch();
      setTaskDialogMessage(null);
      toast({
        title: t('messaging.task.created', 'Task #{{id}} created', { id: result.task_id }),
        description: [result.task_title, result.assigned_to_name].filter(Boolean).join(' · ') || undefined,
        action: result.task_id
          ? { label: t('messaging.task.open', 'Open task'), onClick: () => openTaskFromMessaging(result.task_id) }
          : undefined,
      });
    },
  });

  // Advance an action-required message: pending → in progress → done (or reopen).
  const actionStatusMutation = useMutation({
    mutationFn: ({ messageId, status }: { messageId: number; status: ActionStatus }) =>
      messagingApi.updateActionStatus(messageId, status),
    onSuccess: () => refetch(),
    onError: (error: Error) => {
      notify({
        variant: 'destructive',
        title: t('messaging.action.statusFailed', 'Could not update action status'),
        description: error?.message,
      });
    },
  });

  // Execute workflow action mutation
  const executeActionMutation = useMutation({
    mutationFn: ({ messageId, actionCode, configOverride }: {
      messageId: number;
      actionCode: string;
      configOverride?: Record<string, any>;
    }) => messagingApi.executeMessageAction(messageId, actionCode, configOverride),
    onSuccess: (result) => {
      refetch();
      let title = t('messaging.action.completed', 'Action completed');
      let description: string | undefined;
      if (result.result_entity_type === 'task') {
        title = t('messaging.task.created', 'Task #{{id}} created', { id: result.result_entity_id ?? '' });
        description = result.result_data?.task_title || undefined;
      } else if (result.result_entity_type === 'escalation') {
        title = t('messaging.action.escalated', 'Message escalated');
        description = result.result_data?.escalation_type || undefined;
      } else if (result.result_entity_type === 'followup') {
        title = t('messaging.action.followupScheduled', 'Follow-up scheduled in {{days}} days', {
          days: result.result_data?.followup_days || 3,
        });
      } else if (result.result_entity_type === 'review') {
        title = t('messaging.action.markedForReview', 'Marked for review');
        description = result.result_data?.tag || undefined;
      } else if (result.result_entity_type === 'approval_request') {
        title = t('messaging.action.approvalRequested', 'Approval request created');
      }
      notify({ title, description });
    },
    onError: (error: Error) => {
      notify({
        variant: 'destructive',
        title: t('messaging.action.failed', 'Action failed'),
        description: error?.message,
      });
    },
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    // Scroll the message list itself. scrollIntoView would also scroll every
    // scrollable ancestor (overflow:hidden ones included) and drag the
    // channel header, workspace tabs or the whole page out of view.
    const el = messagesListRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior });
    else el.scrollTop = el.scrollHeight;
  }, []);

  // Only auto-scroll when the message COUNT changes (new message arrived
  // or sent) or another channel opens — not on every refetch (which fires on
  // mark-as-read/unread and 5s polling and would jerk the page on each cycle).
  // Opening a channel jumps straight to the newest message; new ones glide in.
  const lastMessageCountRef = useRef(0);
  const lastChannelIdRef = useRef<number | null>(null);
  useEffect(() => {
    const switched = lastChannelIdRef.current !== channel.id;
    if (switched || messages.length > lastMessageCountRef.current) {
      scrollToBottom(switched || lastMessageCountRef.current === 0 ? 'auto' : 'smooth');
    }
    lastChannelIdRef.current = channel.id;
    lastMessageCountRef.current = messages.length;
  }, [channel.id, messages.length, scrollToBottom]);

  // Keep the floating support launcher off the Send button: the composer is
  // pinned to the bottom of the screen, right where the launcher sits.
  const composerRef = useRef<HTMLDivElement>(null);
  useSupportBubbleClearance(composerRef, !isLoading && canCompose);

  // Keep the newest message in view while the list's height changes (draft
  // attachments, a link preview or reply context appearing in the composer),
  // unless the user has scrolled up to read history.
  useStickToBottom(messagesListRef, !isLoading, stickToBottomRef);

  // Bulk-mark the channel read up to the newest message while it is on screen
  // (page visible + focused); again as new messages arrive while looking.
  useMarkChannelRead(channel.id, isLoading ? null : newestMessageId(rawMessages), {
    onMarked: () => {
      queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
    },
  });

  useEffect(() => {
    if (editingMessage) {
      setMessageInput(editingMessage.content);
      messageInputRef.current?.focus();
    }
  }, [editingMessage]);

  // Debounce the composer's link preview so typing a URL doesn't fire a fetch
  // per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDraftPreviewUrl(firstLinkUrl(messageInput)), 600);
    return () => clearTimeout(handle);
  }, [messageInput]);

  const handleSendMessage = async (scheduleFor?: Date, scheduleZone?: string) => {
    const scheduledAt = scheduleFor instanceof Date ? scheduleFor : undefined;
    const hasContent =
      messageInput.trim() ||
      selectedAttachments.length > 0 ||
      selectedMediaAssets.length > 0 ||
      selectedDocuments.length > 0 ||
      cloudAttachments.length > 0;
    if (!hasContent) return;

    if (editingMessage) {
      if (scheduledAt) return; // Edits are never scheduled.
      editMutation.mutate({ id: editingMessage.id, content: messageInput });
    } else {
      try {
        setIsUploading(true);

        // Upload attachments first if any
        const attachmentIds: number[] = [];
        for (const file of selectedAttachments) {
          try {
            const result = await messagingApi.uploadAttachment(channel.id, file);
            attachmentIds.push(result.id);
          } catch (uploadError) {
            console.error('Failed to upload attachment:', uploadError);
            notify({
              variant: 'destructive',
              title: t('messaging.composer.uploadFailed', 'Could not attach {{name}}', { name: file.name }),
              description: (uploadError as Error)?.message,
            });
            // Continue with other attachments
          }
        }

        // Existing Documents-system files: the server copies the reference into
        // a message attachment and returns the same shape as an upload.
        for (const doc of selectedDocuments) {
          try {
            const result = await messagingApi.attachDocument({
              channel_id: channel.id,
              doc_record_id: doc.docRecordId,
              ...(doc.docFileId ? { doc_file_id: doc.docFileId } : {}),
            });
            attachmentIds.push(result.id);
          } catch (attachError) {
            console.error('Failed to attach document:', attachError);
            notify({
              variant: 'destructive',
              title: t('messaging.composer.uploadFailed', 'Could not attach {{name}}', { name: doc.name }),
              description: (attachError as Error)?.message,
            });
          }
        }

        for (const cloudFile of cloudAttachments) attachmentIds.push(cloudFile.id);

        // Build message content with media library links
        let finalContent = messageInput;
        if (selectedMediaAssets.length > 0) {
          const mediaLinks = selectedMediaAssets.map(asset => {
            const url = asset.short_url || asset.external_url || '';
            return `📎 [${asset.name}](${url})`;
          }).join('\n');
          finalContent = finalContent
            ? `${finalContent}\n\n${mediaLinks}`
            : mediaLinks;
        }

        // Send message with attachment IDs and media asset metadata
        const request: SendMessageRequest = {
          channel_id: channel.id,
          content: finalContent || (attachmentIds.length > 0 ? '[Attachment]' : ''),
          thread_id: replyingTo?.id,
          priority: selectedPriority !== 'normal' && (selectedPriority !== 'urgent' || canSendUrgent) ? selectedPriority : undefined,
          attachments: attachmentIds.length > 0 ? attachmentIds.map(id => ({ id })) : undefined,
          metadata: selectedMediaAssets.length > 0 || previewsDismissed ? {
            ...(selectedMediaAssets.length > 0 ? {
              media_assets: selectedMediaAssets.map(asset => ({
                id: asset.id,
                name: asset.name,
                asset_type: asset.asset_type,
                short_url: asset.short_url,
                thumbnail_url: asset.thumbnail_url,
              })),
            } : {}),
            ...(previewsDismissed ? { suppress_link_previews: true } : {}),
          } : undefined,
          // Action required fields
          is_action_required: isActionRequired || undefined,
          action_due_date: actionDueDate || undefined,
          action_required_description: actionDescription || undefined,
          // TRUS-702: only when the server offered reply options.
          ...(replyChannel ? { delivery_channel: replyChannel } : {}),
          ...(replyChannel === 'email' && emailSubject.trim() ? { subject: emailSubject.trim() } : {}),
        };
        if (scheduledAt) {
          if (noteMode) return; // A note is never scheduled (the menu is hidden too).
          // TRUS-697: the zone the time was picked in (the server defaults to the sender's).
          scheduleMutation.mutate({
            ...request,
            scheduled_at: scheduledAt.toISOString(),
            ...(scheduleZone ? { timezone: scheduleZone } : {}),
          });
          return; // Media chips clear on success, so a failed schedule keeps the draft.
        }
        setDeliveryBlocked(null);
        // Media chips clear on success, so a blocked send keeps the whole draft.
        sendMutation.mutate(request);
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Enter / Cmd-Ctrl+Enter sending is AutoGrowTextarea's job (it also guards IME
  // composition). Here: the @ / # picker gets first claim on navigation keys,
  // and Escape cancels edit / reply.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentions.handleKeyDown(e)) return;
    if (e.key === 'Escape') {
      setEditingMessage(null);
      setReplyingTo(null);
      setMessageInput('');
    }
  };

  const handleReaction = (messageId: number, emoji: string, hasReacted: boolean) => {
    if (hasReacted) {
      removeReactionMutation.mutate({ messageId, emoji });
    } else {
      addReactionMutation.mutate({ messageId, emoji });
    }
    setShowEmojiPicker(null);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedAttachments(prev => [...prev, ...files]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove selected attachment
  const removeAttachment = (index: number) => {
    setSelectedAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle media library selection
  const handleMediaSelect = (asset: MediaAsset) => {
    // Check if already selected
    const isSelected = selectedMediaAssets.some(a => a.id === asset.id);
    if (isSelected) {
      // Remove from selection
      setSelectedMediaAssets(prev => prev.filter(a => a.id !== asset.id));
    } else {
      // Add to selection (max 4)
      if (selectedMediaAssets.length < 4) {
        setSelectedMediaAssets(prev => [...prev, asset]);
      }
    }
  };

  // Remove selected media asset
  const removeMediaAsset = (assetId: number) => {
    setSelectedMediaAssets(prev => prev.filter(a => a.id !== assetId));
  };

  // Typing updates the draft and the @ / # picker.
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
    mentions.handleChange(e);
  };

  // Get file type icon
  const getFileIcon = (file: File) => {
    const type = file.type.split('/')[0];
    switch (type) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'video': return <Film className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      default:
        if (file.name.match(/\.(zip|tar|gz|7z|rar)$/i)) return <Archive className="h-4 w-4" />;
        return <FileText className="h-4 w-4" />;
    }
  };

  const currentUserId = getCurrentUserId() || 0;
  const hasDraft =
    !!messageInput.trim() ||
    selectedAttachments.length > 0 ||
    selectedMediaAssets.length > 0 ||
    selectedDocuments.length > 0 ||
    cloudAttachments.length > 0;

  // Channel management (rename / manage members) only applies to real group
  // channels, never DMs. The current user's channel role gates owner/admin-only
  // actions client-side; the backend enforces the same gate.
  const channelMembers = 'members' in channel ? channel.members : [];
  // TRUS-691: "Schedule meeting" (call button split + composer menu), staff with meetings on.
  const meetingScheduler = useMeetingScheduler({
    channelId: channel.id,
    channelName: channel.display_name || channel.name || undefined,
    channelMembers,
    viewerIsMember,
  });
  const memberNameOf = (userId: number) => channelMembers.find((m) => m.user_id === userId)?.name;
  // A staff sender's name: channel members first, then the org's staff list
  // (in a member DM the replying staff need not be channel members).
  const staffNameOf = (userId: number) =>
    memberNameOf(userId) || (orgUsers as Array<{ id: number; name?: string }>).find((u) => u.id === userId)?.name;
  const myMembership = channelMembers.find((m) => m.user_id === currentUserId);
  const canManageChannel = myMembership?.role === 'owner' || myMembership?.role === 'admin';
  // Urgent bypasses everyone's mute, so it is staff-only: a member (portal)
  // participant never gets the option (the backend rejects it too).
  const canSendUrgent = allowUrgent && myMembership?.member_id == null;
  const isDMChannel = channel.is_dm || channel.channel_type === 'direct' || channel.dm_member_id != null;
  // Read status: the caller's newest message carries the always-visible "Seen" line.
  let latestOwnMessageId: number | null = null;
  for (const m of messages) {
    if (m.sender.user_id === currentUserId && (latestOwnMessageId == null || m.id > latestOwnMessageId)) latestOwnMessageId = m.id;
  }
  // TRUS-668: the newest call message that still says "active" — the header
  // offers Join instead of Start while it is (live state checked by the header).
  let latestActiveCall: CallMessageMeta | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = callMetaOf(messages[i].metadata);
    if (meta) {
      latestActiveCall = meta.status === 'active' ? meta : null;
      break;
    }
  }
  const isGroupChannel = !channel.is_dm && channel.channel_type !== 'direct';
  const isOrgAdmin = useIsOrgAdmin();
  const perms = getChannelPermissions(channel, isOrgAdmin, currentUserId);
  const lifecycle = useChannelLifecycle(
    channel,
    () => {
      setShowDeleteDialog(false);
      onChannelRemoved?.();
    },
    showNotice,
  );
  // Group channels get the full menu; member DMs get Archive only.
  const showChannelMenu = 'members' in channel && (isGroupChannel || perms.isMemberDM);
  // TRUS-638: every conversation (staff DMs too) can be pinned from the header.
  const isPinned = useIsChannelPinned(channel.id);
  const pinActions = useChannelPins();
  const menuTitle = isGroupChannel
    ? t('messaging.channel.settings', 'Channel settings')
    : t('messaging.pins.conversationSettings', 'Conversation settings');

  const openRenameDialog = () => {
    setRenameValue(channel.display_name || channel.name || '');
    setShowRenameDialog(true);
  };

  const submitRename = () => {
    const next = renameValue.trim();
    if (!next || next === (channel.display_name || channel.name)) {
      setShowRenameDialog(false);
      return;
    }
    renameChannelMutation.mutate(next);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    // A fixed-height column: header on top, the message list is the only part
    // that scrolls, the composer stays on screen at the bottom.
    <div className="flex h-full min-h-0 flex-col" data-composer-pane>
      {/* Channel Header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          {isDMChannel ? (
            <AtSign className="h-5 w-5 flex-shrink-0 text-muted-foreground" data-testid="dm-header-icon" />
          ) : (
            <Hash className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
          )}
          <h2 className="truncate font-semibold">{channel.display_name || channel.name}</h2>
          {channel.topic && (
            <span className="hidden truncate text-sm text-muted-foreground sm:inline-block sm:max-w-xs">
              | {channel.topic}
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <CallHeaderButtons
            channelId={channel.id}
            channelName={channel.display_name || channel.name || undefined}
            viewerIsMember={viewerIsMember}
            activeCall={latestActiveCall}
            isMemberDm={channel.dm_member_id != null}
            onScheduleMeeting={meetingScheduler.open}
          />
          <CallNotesButton
            channelId={channel.id}
            channelName={channel.display_name || channel.name || undefined}
            viewerIsMember={viewerIsMember}
            extraTop={<ConversationMeetingsSection channelId={channel.id} />}
          />
          {meetingScheduler.dialog}
          <Badge variant="outline" className="whitespace-nowrap">{channel.member_count} <span className="hidden sm:inline">{t('messaging.channel.membersLabel', 'members')}</span></Badge>
          {onAddPeople && (
            <Button variant="outline" size="sm" className="gap-1" onClick={onAddPeople} title="Add people to this channel">
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('messaging.channel.addPeople', 'Add people')}</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" title={menuTitle}>
                <Settings className="h-4 w-4" />
                <span className="sr-only">{menuTitle}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{menuTitle}</DropdownMenuLabel>
              {isPinned ? (
                <DropdownMenuItem onSelect={() => pinActions.unpin.mutate(channel.id)}>
                  <PinOff className="mr-2 h-4 w-4" />
                  {t('messaging.pins.unpinConversation', 'Unpin conversation')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => pinActions.pin.mutate(channel.id)}>
                  <Pin className="mr-2 h-4 w-4" />
                  {t('messaging.pins.pinConversation', 'Pin conversation')}
                </DropdownMenuItem>
              )}
              {showChannelMenu && isGroupChannel && (
                <DropdownMenuItem onSelect={() => setShowMembersDialog(true)}>
                  <Users className="mr-2 h-4 w-4" />
                  {t('messaging.channel.manageMembers', 'Manage members')}
                </DropdownMenuItem>
              )}
              {showChannelMenu && isGroupChannel && !isDMChannel && (
                <DropdownMenuItem onSelect={() => setShowEventsDialog(true)} data-testid="menu-connect-events">
                  <Zap className="mr-2 h-4 w-4" />
                  {t('messaging.systemEvents.title', 'Connect events')}
                </DropdownMenuItem>
              )}
              {showChannelMenu && isGroupChannel && canManageChannel && (
                <DropdownMenuItem onSelect={openRenameDialog}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('messaging.channel.rename', 'Rename channel')}
                </DropdownMenuItem>
              )}
              {showChannelMenu && (perms.canArchive || perms.canLeave || perms.canDelete) && <DropdownMenuSeparator />}
              {showChannelMenu && perms.canArchive && (
                channel.is_archived ? (
                  <DropdownMenuItem
                    onSelect={() => lifecycle.unarchive.mutate()}
                    disabled={lifecycle.unarchive.isPending}
                  >
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    {t('messaging.channel.unarchive', 'Unarchive channel')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={() => lifecycle.archive.mutate()}
                    disabled={lifecycle.archive.isPending}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    {perms.isMemberDM
                      ? t('messaging.channel.archiveConversation', 'Archive conversation')
                      : t('messaging.channel.archive', 'Archive channel')}
                  </DropdownMenuItem>
                )
              )}
              {showChannelMenu && perms.canLeave && (
                <DropdownMenuItem onSelect={() => lifecycle.leave.mutate()} disabled={lifecycle.leave.isPending}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('messaging.channel.leaveTitle', 'Leave channel')}
                </DropdownMenuItem>
              )}
              {showChannelMenu && perms.canDelete && (
                <DropdownMenuItem
                  onSelect={() => setShowDeleteDialog(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('messaging.channel.delete', 'Delete channel')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DeleteChannelDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) lifecycle.remove.reset();
        }}
        channelName={channel.display_name || channel.name}
        isPending={lifecycle.remove.isPending}
        error={lifecycle.remove.error?.message}
        onConfirm={() => lifecycle.remove.mutate()}
      />

      {/* TRUS-684: system events connected to this channel (never DMs). */}
      {isGroupChannel && !isDMChannel && (
        <ChannelEventsDialog
          channelId={channel.id}
          channelName={channel.display_name || channel.name}
          channelType={channel.channel_type}
          open={showEventsDialog}
          onOpenChange={setShowEventsDialog}
          isChannelManager={canManageChannel}
        />
      )}

      {/* Inline feedback for channel / message actions */}
      {notice && (
        <div className="border-b px-3 py-2 sm:px-4">
          <InlineNotice notice={notice} onClose={clearNotice} />
        </div>
      )}

      {/* Rename channel dialog — owner/admin only (gated above + server-side). */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('messaging.channel.rename', 'Rename channel')}</DialogTitle>
            <DialogDescription>
              {t('messaging.channel.renameHint', "Update the channel's display name. The #handle stays the same.")}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={t('messaging.channel.namePlaceholder', 'Channel name')}
            maxLength={255}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitRename();
              }
            }}
          />
          {renameChannelMutation.error && (
            <InlineNotice
              notice={{
                kind: 'error',
                title: t('messaging.channel.renameFailed', 'Could not rename channel'),
                description: renameChannelMutation.error.message,
              }}
              onClose={() => renameChannelMutation.reset()}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)} disabled={renameChannelMutation.isPending}>
              {t('messaging.channel.cancel', 'Cancel')}
            </Button>
            <Button onClick={submitRename} disabled={renameChannelMutation.isPending || !renameValue.trim()}>
              {renameChannelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('messaging.channel.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage members dialog — lists channel members with a remove control.
          Owners/admins can remove others; anyone can remove (leave) themselves.
          The backend enforces the same rules and blocks removing the owner. */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('messaging.channel.members', 'Channel members')}</DialogTitle>
            <DialogDescription>
              {canManageChannel
                ? t('messaging.channel.membersHintManager', 'Remove people from this channel. Channel owners cannot be removed.')
                : t('messaging.channel.membersHintSelf', 'You can remove yourself from this channel.')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {channelMembers.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{t('messaging.channel.noMembers', 'No members to show.')}</div>
            )}
            {channelMembers.map((m) => {
              const isSelf = m.user_id === currentUserId;
              // Mirror the server gate: owner/admin may remove others (admins
              // cannot remove owners/admins), and anyone may remove themselves.
              const canRemove =
                m.role !== 'owner' &&
                (isSelf ||
                  myMembership?.role === 'owner' ||
                  (myMembership?.role === 'admin' && m.role !== 'admin'));
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-muted/50">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm">{m.name}</span>
                    {m.role !== 'member' && (
                      <Badge variant="outline" className="text-xs capitalize">{m.role}</Badge>
                    )}
                    {isSelf && <span className="text-xs text-muted-foreground">{t('messaging.channel.you', '(you)')}</span>}
                  </div>
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-destructive hover:text-destructive"
                      onClick={() => removeMemberMutation.mutate(m.id)}
                      disabled={removeMemberMutation.isPending}
                      title={isSelf ? t('messaging.channel.leaveTitle', 'Leave channel') : t('messaging.channel.removeTitle', 'Remove {{name}}', { name: m.name })}
                    >
                      <UserMinus className="h-4 w-4" />
                      <span className="hidden sm:inline">{isSelf ? t('messaging.channel.leave', 'Leave') : t('messaging.channel.remove', 'Remove')}</span>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          {removeMemberMutation.error && (
            <InlineNotice
              notice={{
                kind: 'error',
                title: t('messaging.channel.removeFailed', 'Could not remove member'),
                description: removeMemberMutation.error.message,
              }}
              onClose={() => removeMemberMutation.reset()}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersDialog(false)}>{t('messaging.channel.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRUS-709: owners (add / replace / remove) and who has replied — staff only. */}
      {channel.dm_member_id != null && !viewerIsMember && <ConversationOwnersBar channel={channel} />}

      {/* Pinned Messages Section */}
      {pinnedMessages.length > 0 && (
        <div className="border-b bg-yellow-50 dark:bg-yellow-900/20">
          <div className="px-4 py-2 flex items-center gap-2 border-b border-yellow-200 dark:border-yellow-800">
            <Pin className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
              Pinned Messages ({pinnedMessages.length})
            </span>
          </div>
          <div className="max-h-48 overflow-y-auto p-2 space-y-2">
            {pinnedMessages.map((message) => (
              <div
                key={`pinned-${message.id}`}
                className="flex items-start gap-2 p-2 bg-white dark:bg-slate-800 rounded border border-yellow-200 dark:border-yellow-700 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
                onClick={() => {
                  // Scroll to the message in the main list
                  const element = document.getElementById(`message-${message.id}`);
                  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                <Pin className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{message.sender.name}</span>
                    <span>{t('messaging.thread.pinnedAMessage', 'pinned a message')}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {message.content}
                  </p>
                </div>
                <button
                  className="p-1 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    unpinMutation.mutate(message.id);
                  }}
                  title="Unpin message"
                >
                  <X className="h-3 w-3 text-gray-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {/* When the support launcher is lifted above the composer it floats over
          the list's bottom-right corner — exactly where the newest message's
          "Seen" line sits — so the list keeps that much room below it. */}
      <div
        ref={messagesListRef}
        // overflow-x-hidden: a message swiped to reply never scrolls the list sideways.
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-4"
        style={{ paddingBottom: 'calc(1rem + min(var(--tsw-launcher-lift, 0px), 80px))' }}
        data-testid="message-list"
      >
        {messages.length === 0 && pinnedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-4" />
            <p className="text-lg font-medium">{t('messaging.thread.emptyTitle', 'No messages yet')}</p>
            <p className="text-sm">{t('messaging.thread.emptyHint', 'Start the conversation!')}</p>
          </div>
        ) : (
          // TRUS-702: a member viewer never sees internal notes (the server
          // filters them too).
          messages.filter((m) => !(viewerIsMember && isInternalNote(m))).map((message, index) => (
            <MessageItem
              isMemberConversation={channel.dm_member_id != null}
              isDM={isDMChannel}
              isLatestOwn={message.id === latestOwnMessageId}
              key={message.id}
              message={message}
              currentUserId={currentUserId}
              showAvatar={true}  // Always show avatar for timeline view
              onReply={() => {
                setReplyingTo(message);
                messageInputRef.current?.focus();
              }}
              canSwipeReply={canCompose}
              onEdit={() => setEditingMessage(message)}
              onDelete={() => deleteMutation.mutate(message.id)}
              onPin={() =>
                message.is_pinned
                  ? unpinMutation.mutate(message.id)
                  : pinMutation.mutate(message.id)
              }
              onReaction={(emoji, hasReacted) =>
                handleReaction(message.id, emoji, hasReacted)
              }
              nameOf={memberNameOf}
              senderNameOf={staffNameOf}
              onOpenThread={() => onOpenThread?.(message)}
              showEmojiPicker={showEmojiPicker === message.id}
              onToggleEmojiPicker={() =>
                setShowEmojiPicker(showEmojiPicker === message.id ? null : message.id)
              }
              onSetPriority={(priority) =>
                setPriorityMutation.mutate({ messageId: message.id, priority })
              }
              onMarkRead={() => markReadMutation.mutate(message.id)}
              onMarkUnread={() => markUnreadMutation.mutate(message.id)}
              onCreateTask={() => setTaskDialogMessage(message)}
              onSetActionStatus={(status) =>
                actionStatusMutation.mutate({ messageId: message.id, status })
              }
              onExecuteAction={(actionCode, configOverride) => {
                executeActionMutation.mutate({
                  messageId: message.id,
                  actionCode,
                  configOverride
                });
              }}
            />
          ))
        )}
      </div>

      {/* TRUS-719: who else is typing in this member conversation. */}
      {isMemberDm && !viewerIsMember && <TypingIndicator entries={typingIndicator.typing} />}

      {/* TRUS-709: another staff member replied since this viewer opened the
          member conversation. */}
      {isMemberDm && !viewerIsMember && (
        <StaffReplyBanner
          channelId={channel.id}
          messages={messages}
          myUserId={currentUserId || null}
          nameOf={staffNameOf}
          onShow={(messageId) => {
            const el = messageId != null ? document.getElementById(`message-${messageId}`) : null;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            else scrollToBottom();
          }}
        />
      )}

      {/* Draft extras (reply context, pending attachments, link preview). Capped
          so a long list of them scrolls here instead of pushing the input row
          off screen; the message list gives up the space. */}
      <div className="max-h-[40%] min-h-0 flex-shrink-0 overflow-y-auto">
      {/* Reply/Edit Context */}
      {(replyingTo || editingMessage) && (
        <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {replyingTo && (
              <>
                <Reply className="h-4 w-4" />
                <span>{t('messaging.composer.replyingTo', 'Replying to {{name}}', { name: replyingTo.sender.name })}</span>
              </>
            )}
            {editingMessage && (
              <>
                <Pencil className="h-4 w-4" />
                <span>{t('messaging.composer.editing', 'Editing message')}</span>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setReplyingTo(null);
              setEditingMessage(null);
              setMessageInput('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Selected Attachments Preview */}
      {selectedAttachments.length > 0 && (
        <div className="px-4 py-2 border-t bg-slate-50 dark:bg-slate-900">
          <div className="flex flex-wrap gap-2">
            {selectedAttachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white dark:bg-slate-800 border rounded px-2 py-1 text-sm"
              >
                {getFileIcon(file)}
                <span className="truncate max-w-[150px]">{file.name}</span>
                <span className="text-muted-foreground text-xs">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
                <button
                  onClick={() => removeAttachment(index)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Media Library Assets Preview */}
      {selectedMediaAssets.length > 0 && (
        <div className="px-4 py-2 border-t bg-purple-50 dark:bg-purple-900/20">
          <div className="flex flex-wrap gap-2">
            {selectedMediaAssets.map((asset) => (
              <div
                key={asset.id}
                className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 rounded px-2 py-1 text-sm"
              >
                <LinkIcon className="h-4 w-4 text-purple-500" />
                <span className="truncate max-w-[150px]">{asset.name}</span>
                {asset.short_url && (
                  <span className="text-green-600 dark:text-green-400 text-xs">
                    (trackable)
                  </span>
                )}
                <button
                  onClick={() => removeMediaAsset(asset.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Documents-system files */}
      {selectedDocuments.length > 0 && (
        <div className="px-4 py-2 border-t bg-slate-50 dark:bg-slate-900">
          <div className="flex flex-wrap gap-2">
            {selectedDocuments.map((doc) => (
              <div
                key={doc.docRecordId}
                className="flex items-center gap-2 bg-white dark:bg-slate-800 border rounded px-2 py-1 text-sm"
              >
                <FileText className="h-4 w-4" />
                <span className="truncate max-w-[150px]">{doc.name}</span>
                <button
                  onClick={() =>
                    setSelectedDocuments((prev) => prev.filter((d) => d.docRecordId !== doc.docRecordId))
                  }
                  className="text-muted-foreground hover:text-destructive"
                  title={t('messaging.channel.remove', 'Remove')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Google Drive / OneDrive / SharePoint links */}
      {cloudAttachments.length > 0 && (
        <div className="px-4 py-2 border-t bg-slate-50 dark:bg-slate-900" data-testid="pending-cloud-attachments">
          <div className="flex flex-wrap gap-2">
            {cloudAttachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-2 bg-white dark:bg-slate-800 border rounded px-2 py-1 text-sm"
              >
                <ProviderMark provider={att.provider || 'google_drive'} />
                <span className="truncate max-w-[180px]">{att.filename}</span>
                <button
                  onClick={() => setCloudAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                  className="text-muted-foreground hover:text-destructive"
                  title={t('messaging.channel.remove', 'Remove')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live link preview for the draft — dismissing it sends the message with
          metadata.suppress_link_previews so nobody sees a card. */}
      {canCompose && draftPreviewUrl && !previewsDismissed && !editingMessage && (
        <div className="px-4 pt-2">
          <LinkPreviewCard url={draftPreviewUrl} onDismiss={() => setPreviewsDismissed(true)} className="mt-0" />
        </div>
      )}
      </div>

      {/* Urgent draft: say plainly what it does before it is sent. */}
      {canCompose && selectedPriority === 'urgent' && canSendUrgent && !editingMessage && (
        <div
          className="mx-4 mt-2 flex flex-shrink-0 items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          data-testid="urgent-hint"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">
            <strong>{t('messaging.urgent.label', 'Urgent')}</strong>
            {' · '}
            {t('messaging.urgent.hint', 'Urgent messages notify everyone, even if they muted this channel.')}
          </span>
          <button
            type="button"
            onClick={() => setSelectedPriority('normal')}
            className="rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40"
            aria-label={t('messaging.urgent.clear', 'Not urgent')}
            title={t('messaging.urgent.clear', 'Not urgent')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* The caller's scheduled messages for this channel. */}
      {canCompose && (
        <ScheduledMessagesChip channelId={channel.id} onSentNow={() => { refetch(); scrollToBottom(); }} />
      )}

      {/* Message Input */}
      {!canCompose ? (
        <div className="flex-shrink-0 p-4 border-t bg-slate-50 dark:bg-slate-900 text-center">
          <p className="text-sm text-muted-foreground">
            {isReadonly
              ? 'This channel is read-only.'
              : 'You do not have permission to post in this channel.'}
          </p>
        </div>
      ) : (
      <div
        ref={composerRef}
        className={cn(
          'flex-shrink-0 p-4 border-t',
          noteMode && 'border-t-2 border-yellow-300 bg-yellow-50/70 dark:border-yellow-800 dark:bg-yellow-950/20',
        )}
        data-testid="message-composer"
        data-reply-channel={replyChannel ?? undefined}
      >
        {/* TRUS-702: reply channel (member DMs, when the server offers options). */}
        {replyOptions && replyChannel && !editingMessage && (
          <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="reply-channel-row">
            <span className="text-xs text-muted-foreground">{t('messaging.replyChannel.pickerLabel', 'Reply via')}</span>
            <ReplyChannelPicker
              options={replyOptions.options}
              value={replyChannel}
              onChange={(c) => {
                setPickedReplyChannel(c);
                setDeliveryBlocked(null);
              }}
              disabled={sendMutation.isPending || isUploading}
            />
            {noteMode && (
              <span className="text-xs font-medium text-yellow-800 dark:text-yellow-200" data-testid="note-hint">
                {t('messaging.replyChannel.noteHint', 'Only staff see notes')}
              </span>
            )}
          </div>
        )}
        {replyChannel === 'email' && !editingMessage && (
          <Input
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder={t('messaging.replyChannel.subjectPlaceholder', 'Subject')}
            aria-label={t('messaging.replyChannel.subject', 'Email subject')}
            className="mb-2 h-8 text-sm"
            data-testid="email-subject"
          />
        )}
        {deliveryBlocked && (
          <div
            className="mb-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
            data-testid="delivery-blocked"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span className="flex-1">
              <strong>{t('messaging.replyChannel.blockedTitle', 'Not sent')}</strong>
              {' · '}
              {blockReasonText(deliveryBlocked.reason, t)}
              {deliveryBlocked.detail ? ` (${deliveryBlocked.detail})` : ''}
            </span>
            <button
              type="button"
              onClick={() => setDeliveryBlocked(null)}
              className="rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40"
              aria-label={t('messaging.replyChannel.dismiss', 'Dismiss')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {/* items-end: as the message box grows, the buttons stay on its last line. */}
        <div className="flex items-end gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.tar,.gz"
          />
          <AttachMenu
            channelId={channel.id}
            disabled={isUploading}
            onUploadClick={() => fileInputRef.current?.click()}
            onDocumentsClick={() => setShowDocPicker(true)}
            onMediaClick={() => setShowMediaLibrary(true)}
            onCloudAttached={(att) =>
              setCloudAttachments((prev) => (prev.some((a) => a.id === att.id) ? prev : [...prev, att]))
            }
            onError={({ title, description }) => notify({ variant: 'destructive', title, description })}
            onScheduleMeeting={meetingScheduler.open}
          />
          <InsertLinkControl link={composerLink} disabled={isUploading} />
          <div className="flex-1 relative">
            <AutoGrowTextarea
              ref={messageInputRef}
              value={messageInput}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (composerLink.inputProps.onKeyDown(e)) return;
                handleKeyDown(e);
              }}
              onSubmit={handleSendMessage}
              onFocus={() => setComposerFocused(true)}
              onBlur={() => setComposerFocused(false)}
              onPaste={composerLink.inputProps.onPaste}
              data-claims-shortcuts={composerLink.inputProps['data-claims-shortcuts']}
              placeholder={
                noteMode
                  ? t('messaging.replyChannel.notePlaceholder', 'Add an internal note')
                  : // A DM's channel.name is internal (dm-<a>-<b>[-suffix]): address the person.
                isDMChannel
                  ? t('messaging.thread.messagePerson', 'Message {{name}}', {
                      name: channel.display_name || t('messaging.thread.thisConversation', 'this conversation'),
                    })
                  : t('messaging.thread.messageChannel', 'Message #{{name}}', { name: channel.name })
              }
              className="pr-10"
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 bottom-0.5 text-muted-foreground"
              onClick={mentions.openAtCaret}
            >
              <AtSign className="h-4 w-4" />
            </Button>

            {/* @ people / members and # channel picker */}
            <MentionPicker composer={mentions} />
          </div>

          {/* Priority Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={selectedPriority === 'urgent' ? 'danger' : selectedPriority !== 'normal' ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  "gap-1",
                  selectedPriority === 'high' && "bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-300",
                )}
                title={t('messaging.urgent.priorityTitle', 'Set message priority')}
                aria-label={t('messaging.urgent.priorityTitle', 'Set message priority')}
                data-testid="priority-trigger"
              >
                {selectedPriority === 'urgent' ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : selectedPriority === 'high' ? (
                  <Flag className="h-4 w-4" />
                ) : (
                  <Flag className="h-4 w-4 text-muted-foreground" />
                )}
                {selectedPriority === 'urgent' && (
                  <span className="text-xs font-semibold">{t('messaging.urgent.label', 'Urgent')}</span>
                )}
                {selectedPriority === 'high' && (
                  <span className="text-xs">{PRIORITY_CONFIG.high.label}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Message Priority</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setSelectedPriority('normal')}
                className={selectedPriority === 'normal' ? 'bg-accent' : ''}
              >
                <span className="mr-2 text-gray-400">○</span> Normal
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSelectedPriority('high')}
                className={selectedPriority === 'high' ? 'bg-accent' : ''}
              >
                <Flag className="mr-2 h-4 w-4 text-orange-500" /> High Priority
              </DropdownMenuItem>
              {canSendUrgent && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setSelectedPriority(selectedPriority === 'urgent' ? 'normal' : 'urgent')}
                    className={cn(
                      'flex flex-col items-start gap-0.5 text-red-700 focus:text-red-800 dark:text-red-300',
                      selectedPriority === 'urgent' && 'bg-red-50 dark:bg-red-950/40',
                    )}
                    data-testid="mark-urgent"
                  >
                    <span className="flex items-center font-medium">
                      <AlertTriangle className="mr-2 h-4 w-4 text-red-500" />
                      {selectedPriority === 'urgent'
                        ? t('messaging.urgent.clear', 'Not urgent')
                        : t('messaging.urgent.mark', 'Mark as urgent')}
                    </span>
                    <span className="ml-6 max-w-[14rem] text-xs font-normal text-muted-foreground">
                      {t('messaging.urgent.hint', 'Urgent messages notify everyone, even if they muted this channel.')}
                    </span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Action Required Toggle - Simple button with popover */}
          <div className="relative">
            <Button
              variant={isActionRequired ? 'default' : 'ghost'}
              size="sm"
              className={cn(
                "gap-1",
                isActionRequired && "bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300"
              )}
              title="Toggle action required"
              onClick={() => setIsActionRequired(!isActionRequired)}
            >
              <CheckSquare className={cn("h-4 w-4", !isActionRequired && "text-muted-foreground")} />
              {isActionRequired && <span className="text-xs">Action</span>}
            </Button>
          </div>

          {/* Action Required Details (shown when action required) */}
          {isActionRequired && (
            <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              {/* Action Description */}
              <input
                type="text"
                value={actionDescription}
                onChange={(e) => setActionDescription(e.target.value)}
                placeholder="What action is needed?"
                className="flex-1 min-w-[150px] px-2 py-1 text-sm bg-white dark:bg-slate-800 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {/* Due Date */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-blue-600" />
                <input
                  type="date"
                  value={actionDueDate}
                  onChange={(e) => setActionDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  placeholder="Due date"
                  className="w-32 px-2 py-1 text-xs bg-white dark:bg-slate-800 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Split Send: the button sends now, the caret schedules. */}
          <div className="flex flex-shrink-0 items-stretch" data-testid="send-split">
            <Button
              variant={selectedPriority === 'urgent' && canSendUrgent ? 'danger' : 'default'}
              onClick={() => handleSendMessage()}
              disabled={!hasDraft || sendMutation.isPending || scheduleMutation.isPending || isUploading}
              className={cn(!editingMessage && !noteMode && 'rounded-r-none')}
              aria-label={t('messaging.composer.send', 'Send')}
              title={t('messaging.composer.send', 'Send')}
              data-testid="send-button"
            >
              <Send className="h-4 w-4" />
            </Button>
            {!editingMessage && !noteMode && (
              <ScheduleSendMenu
                urgent={selectedPriority === 'urgent' && canSendUrgent}
                disabled={!hasDraft || sendMutation.isPending || scheduleMutation.isPending || isUploading}
                onSchedule={(when, tz) => handleSendMessage(when, tz)}
              />
            )}
          </div>
        </div>
      </div>
      )}

      {/* Create Task dialog — the backend resolves the member (dm_member_id,
          sender, channel member) when we don't pass one. */}
      <CreateTaskDialog
        message={taskDialogMessage}
        onOpenChange={(open) => {
          if (!open) {
            setTaskDialogMessage(null);
            createTaskMutation.reset();
          }
        }}
        users={orgUsers}
        memberId={channelMemberId ?? channel.dm_member_id ?? null}
        isPending={createTaskMutation.isPending}
        error={createTaskMutation.error?.message}
        onSubmit={(request) =>
          taskDialogMessage && createTaskMutation.mutate({ messageId: taskDialogMessage.id, request })
        }
      />

      <DocumentPickerDialog
        open={showDocPicker}
        onOpenChange={setShowDocPicker}
        memberId={channel.dm_member_id ?? null}
        onConfirm={(docs) =>
          setSelectedDocuments((prev) => [
            ...prev,
            ...docs.filter((d) => !prev.some((p) => p.docRecordId === d.docRecordId)),
          ])
        }
      />

      {/* Media Library Selector Modal */}
      {showMediaLibrary && (
        <MediaLibrarySelector
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaLibrary(false)}
          selectedIds={selectedMediaAssets.map(a => a.id)}
          maxSelection={4}
        />
      )}
    </div>
  );
}

interface MessageItemProps {
  message: Message;
  currentUserId: number;
  showAvatar: boolean;
  isMemberConversation?: boolean;
  /** One-to-one conversation: read status reads "Seen 2:14 PM" / "Delivered". */
  isDM?: boolean;
  /** The caller's newest message — its read status is always shown. */
  isLatestOwn?: boolean;
  onReply: () => void;
  /** TRUS-759: swipe the message right (touch) to reply - only where the caller can post. */
  canSwipeReply?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPin: () => void;
  onReaction: (emoji: string, hasReacted: boolean) => void;
  onOpenThread: () => void;
  showEmojiPicker: boolean;
  onToggleEmojiPicker: () => void;
  onSetPriority: (priority: MessagePriority) => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onCreateTask: () => void;
  onSetActionStatus: (status: ActionStatus) => void;
  onExecuteAction: (actionCode: string, configOverride?: Record<string, any>) => void;
  /** Resolve a user id to a name (reaction tooltips). */
  nameOf?: (userId: number) => string | undefined;
  /** Resolve a staff user id to a name (sender labels in a member DM). */
  senderNameOf?: (userId: number) => string | undefined;
}

function MessageItem({
  message,
  currentUserId,
  showAvatar,
  isMemberConversation,
  isDM = false,
  isLatestOwn = false,
  onReply,
  canSwipeReply = false,
  onEdit,
  onDelete,
  onPin,
  onReaction,
  onOpenThread,
  showEmojiPicker,
  onToggleEmojiPicker,
  onSetPriority,
  onMarkRead,
  onMarkUnread,
  onCreateTask,
  onSetActionStatus,
  onExecuteAction,
  nameOf,
  senderNameOf,
}: MessageItemProps) {
  const { t } = useAppTranslation();
  const isOwnMessage = message.sender.user_id === currentUserId;
  // The member (client) vs staff — color-coded so a conversation reads clearly.
  const isMember = message.sender.member_id != null;
  // TRUS-709: in a member DM every staff message says who sent it — several
  // staff may answer the same member — never a bare "Staff" when it is known.
  const senderName =
    isMemberConversation && !isMember
      ? resolveStaffSenderName(message, senderNameOf ?? nameOf) ?? t('messaging.sender.staff', 'Staff')
      : message.sender.name;
  const showHeader = showStaffName(!!isMemberConversation, isMember, showAvatar);
  // True two-sided (iMessage-style) layout: in a member conversation, the member
  // sits on the LEFT and ALL staff on the RIGHT (regardless of which staff / viewer).
  // In a regular staff channel, fall back to own-on-right.
  // TRUS-684: a WeaveIn bot post (metadata.system_event) always sits on the left.
  const systemEvent = systemEventMetaOf(message.metadata);
  // TRUS-702: a staff-only internal note gets its own (yellow) look, never blue.
  const isNote = isInternalNote(message);
  const onRight = systemEvent ? false : isMemberConversation ? !isMember : isOwnMessage;
  const onBlue = onRight && !isMember && !isNote; // staff bubble = blue + white text
  const messageChannel = isMemberConversation ? normalizeChannel(message.channel) ?? (isNote ? 'note' : null) : null;
  // Delivery status: outbound (staff → member) SMS / email / campaign only.
  const showDelivery =
    isMemberConversation &&
    !isMember &&
    (messageChannel === 'sms' || messageChannel === 'email' || messageChannel === 'campaign') &&
    !!message.delivery_status;
  // TRUS-703: a row mirrored into the member DM from another path — a campaign /
  // phone call / voicemail card, or an SMS / email with "Sent from <source>".
  // Unknown card types → null → the plain bubble below.
  const dmCard = isMemberConversation ? memberDmCardOf(message.metadata) : null;
  // A card row gets a caption only for sources its card does not name (a robocall, TRUS-719).
  const mirroredSource = isMemberConversation
    ? dmCard
      ? cardCaptionSourceOf(message.metadata)
      : mirroredSourceOf(message.metadata)
    : null;
  const [showActions, setShowActions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [actionTemplates, setActionTemplates] = useState<MessageActionTemplate[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const priorityConfig = PRIORITY_CONFIG[message.priority || 'normal'] || PRIORITY_CONFIG.normal;
  const isUrgent = message.priority === 'urgent';

  // Fetch action templates when dropdown opens
  const fetchActionTemplates = async () => {
    if (actionTemplates.length > 0 || loadingActions) return;
    setLoadingActions(true);
    try {
      const templates = await messagingApi.getMessageAvailableActions(message.id);
      setActionTemplates(templates);
    } catch (error) {
      console.error('Failed to fetch action templates:', error);
    } finally {
      setLoadingActions(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const hasReacted = (emoji: string) => {
    return message.reactions[emoji]?.includes(currentUserId);
  };

  const hasReactions = Object.values(message.reactions || {}).some((users) => users && users.length > 0);

  // Check if this is a reply (has thread_id)
  const isReply = !!message.thread_id;

  // Check if message is unread (only for non-own messages)
  const isUnread = !isOwnMessage && message.is_read === false;

  // TRUS-759: touch screens have no hover, so the action toolbar opens on a
  // long-press or a tap on the message, and closes on a tap anywhere else.
  // Swiping the message right replies to it (not system / bot posts).
  const rowRef = useRef<HTMLDivElement | null>(null);
  const press = useRef<{ id: number; x: number; y: number; timer: number | null; long: boolean } | null>(null);
  // TRUS-761: the user can turn swipe-to-reply off; sensitivity scales the 64px distance.
  const swipeSettings = useSwipeSettings();
  const canReplyBySwipe =
    canSwipeReply && swipeSettings.messages.right === 'reply' && message.content_type !== 'system' && !systemEvent;
  const swipe = useSwipeActions({
    onSwipeRight: canReplyBySwipe ? onReply : undefined,
    commitPx: Math.round((64 * SENSITIVITY_RATIO[swipeSettings.sensitivity]) / SENSITIVITY_RATIO.medium),
    trackpad: swipeSettings.trackpad,
    trackpadReverse: swipeSettings.trackpadReverse,
  });
  const setRowRef = (el: HTMLDivElement | null) => {
    rowRef.current = el;
    swipe.ref.current = el;
  };
  const clearPress = () => {
    if (press.current?.timer != null) window.clearTimeout(press.current.timer);
    press.current = null;
  };
  useEffect(() => clearPress, []);
  useEffect(() => {
    if (!showActions) return;
    const outside = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      if (rowRef.current && e.target instanceof Node && rowRef.current.contains(e.target)) return;
      setShowActions(false);
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [showActions]);
  const touchHandlers = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      swipe.handlers.onPointerDown(e);
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      clearPress();
      const entry = { id: e.pointerId, x: e.clientX, y: e.clientY, timer: null as number | null, long: false };
      entry.timer = window.setTimeout(() => {
        entry.long = true;
        setShowActions(true);
      }, LONG_PRESS_MS);
      press.current = entry;
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      swipe.handlers.onPointerMove(e);
      const p = press.current;
      if (p && p.id === e.pointerId && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) clearPress();
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
      swipe.handlers.onPointerUp(e);
      const p = press.current;
      if (p && p.id === e.pointerId) {
        // A short tap on the message (not on a link or button inside it) toggles the toolbar.
        const interactive = e.target instanceof Element && !!e.target.closest('a,button,input,textarea,select,[role="menuitem"]');
        if (!p.long && !interactive) setShowActions((v) => !v);
        clearPress();
      }
    },
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => {
      swipe.handlers.onPointerCancel(e);
      clearPress();
    },
    onClickCapture: swipe.handlers.onClickCapture,
    // A long-press would otherwise also open the browser's own menu.
    onContextMenu: (e: React.MouseEvent<HTMLDivElement>) => {
      if (press.current?.long) e.preventDefault();
    },
  };

  return (
    <div
      id={`message-${message.id}`}
      ref={setRowRef}
      data-testid={`message-row-${message.id}`}
      className={cn(
        'group relative flex w-full',
        onRight ? 'justify-end' : 'justify-start',
        message.is_pinned && 'bg-yellow-50 dark:bg-yellow-900/20 -mx-4 px-4 py-1 rounded',
        isReply && 'ml-8',
        // Room for the reaction pills that overlap the bubble's top corner.
        hasReactions && REACTION_ROW_CLEARANCE_CLASS
      )}
      style={{
        touchAction: canReplyBySwipe ? 'pan-y' : undefined,
        transform: swipe.state.offset ? `translate3d(${Math.min(swipe.state.offset, 96)}px,0,0)` : undefined,
        transition: swipe.state.dragging ? 'none' : 'transform 180ms ease-out',
      }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      {...touchHandlers}
    >
      {swipe.state.side === 'right' && (
        <span
          aria-hidden="true"
          data-testid="swipe-reply-hint"
          className={cn(
            'absolute -left-10 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
            swipe.state.armed && 'bg-blue-500 text-white',
          )}
        >
          <Reply className="h-4 w-4" />
        </span>
      )}

      <div className={cn(
        'flex gap-2 max-w-[75%]',
        onRight ? 'flex-row-reverse' : 'flex-row'
      )}>
        {/* Unread indicator - positioned next to avatar */}
        {isUnread && (
          <div className="flex items-center flex-shrink-0">
            <div
              className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-blue-200 shadow-sm shadow-blue-500/50"
              title="Unread message"
            />
          </div>
        )}

        {/* Avatar - show on the left side (member / incoming) */}
        {!onRight && showAvatar && (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0 relative">
            {systemEvent ? (
              <Bot className="h-4 w-4" aria-hidden="true" />
            ) : message.sender.avatar ? (
              <img
                src={message.sender.avatar}
                alt={message.sender.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(message.sender.name)
            )}
          </div>
        )}
        {!onRight && !showAvatar && <div className="w-8 flex-shrink-0" />}

        {/* Bubble + its read status underneath (own messages). */}
        <div className={cn('flex min-w-0 flex-col', onRight ? 'items-end' : 'items-start')}>
        {/* Message Bubble — member (client) = amber on the LEFT, staff = blue on the
            RIGHT (true two-sided thread). */}
        <div data-testid="message-bubble" data-urgent={isUrgent || undefined} data-note={isNote || undefined} className={cn(
          'relative rounded-2xl px-4 py-2 min-w-0 max-w-full',
          isNote
            ? 'bg-yellow-50 text-yellow-950 rounded-br-sm border border-dashed border-yellow-400 dark:bg-yellow-950/30 dark:text-yellow-100 dark:border-yellow-700'
            : isMember
            ? 'bg-amber-100 text-amber-950 rounded-bl-sm border border-amber-200 dark:bg-amber-900/30 dark:text-amber-100'
            : onRight
              ? 'bg-blue-500 text-white rounded-br-sm'
              : 'bg-muted rounded-bl-sm',
          message.is_pinned && 'border-2 border-yellow-400',
          // Urgent: a red accent on the bubble's outer edge, on either side.
          isUrgent && !message.is_pinned && 'ring-2 ring-red-500 ring-offset-1 ring-offset-background',
        )}>
          {/* Sender name + role badge — shown on both sides so it's clear who replied */}
          {showHeader && (
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {systemEvent ? (
                <SystemEventSender />
              ) : (
                <>
              <span className={cn("font-semibold text-sm", onBlue && "text-white")} data-testid="message-sender-name">{senderName}</span>
              <Badge variant="outline" className={cn(
                "text-[10px] px-1.5 py-0",
                isMember ? "border-amber-300 bg-amber-50 text-amber-700" : "border-blue-300 bg-blue-50 text-blue-700"
              )}>
                {isMember ? t('messaging.sender.memberBadge', 'Member') : t('messaging.sender.staffBadge', 'Staff')}
              </Badge>
                </>
              )}
              {messageChannel && <MessageChannelBadge channel={messageChannel} />}
              {isUrgent ? (
                <span
                  className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white shadow-sm"
                  data-testid="urgent-label"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {t('messaging.urgent.label', 'Urgent')}
                </span>
              ) : message.priority && message.priority !== 'normal' && (
                <Badge variant="outline" className={cn("text-xs", priorityConfig.bgColor, priorityConfig.color)}>
                  {priorityConfig.icon && <priorityConfig.icon className="h-3 w-3 mr-1" />}
                  {priorityConfig.label}
                </Badge>
              )}
            </div>
          )}

          {/* Message Content (no "[Attachment]" placeholder line for a file-only message).
              A call message (metadata.call, TRUS-668) renders as a call card instead,
              a call-notes message (metadata.call_notes, v2) as a notes card. */}
          {systemEvent ? (
            <SystemEventCard meta={systemEvent} />
          ) : meetingMetaOf(message.metadata) ? (
            <MeetingMessageCard
              meta={meetingMetaOf(message.metadata)!}
              currentUserId={currentUserId}
              tone={onBlue ? 'inverted' : 'default'}
            />
          ) : callMetaOf(message.metadata) ? (
            <CallMessageCard
              meta={callMetaOf(message.metadata)!}
              channelId={message.channel_id}
              tone={onBlue ? 'inverted' : 'default'}
            />
          ) : callNotesMetaOf(message.metadata) ? (
            <CallNotesCard meta={callNotesMetaOf(message.metadata)!} tone={onBlue ? 'inverted' : 'default'} />
          ) : dmCard ? (
            <MemberDmCardView
              metadata={message.metadata}
              body={message.content}
              viewer="staff"
              tone={onBlue ? 'inverted' : 'default'}
              nameOf={nameOf}
              channelId={message.channel_id}
              messageId={message.id}
            />
          ) : visibleMessageText(message.content, !!message.attachments?.length) && (
            <div className={cn(
              "text-sm whitespace-pre-wrap break-words",
              onBlue && "text-white"
            )}>
              <MessageText content={message.content} tone={onBlue ? 'inverted' : 'default'} />
            </div>
          )}

          {/* Link previews (first 3 unique URLs) unless the sender dismissed them */}
          {!systemEvent && !dmCard && (
            <MessageLinkPreviews content={message.content} metadata={message.metadata} suppressed={message.suppress_link_previews} />
          )}

          {/* Timestamp and status badges */}
          <div className={cn(
            "flex items-center gap-2 mt-1 flex-wrap",
            onRight ? "justify-end" : "justify-start"
          )}>
            <span className={cn(
              "text-xs",
              onBlue ? "text-white/70" : "text-muted-foreground"
            )}>
              {formatLocalDateTime(message.created_at)}
            </span>
            {showDelivery && <DeliveryStatusBadge status={message.delivery_status} inverted={onBlue} />}
            {mirroredSource && <MirroredSourceCaption source={mirroredSource} tone={onBlue ? 'inverted' : 'default'} />}
            {message.is_edited && (
              <span className={cn(
                "text-xs",
                onBlue ? "text-white/70" : "text-muted-foreground"
              )}>(edited)</span>
            )}
            {message.is_pinned && (
              <Pin className={cn("h-3 w-3", onBlue ? "text-white/70" : "text-yellow-500")} />
            )}
            {message.linked_task_id && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openTaskFromMessaging(message.linked_task_id as number);
                }}
                title={t('messaging.task.open', 'Open task')}
                className="rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer">
                  <ClipboardList className="h-3 w-3 mr-1" />
                  {t('messaging.task.badge', 'Task #{{id}}', { id: message.linked_task_id })}
                </Badge>
              </button>
            )}
            {message.is_action_required && (
              <Badge variant="outline" className={cn(
                "text-xs",
                message.action_status === 'completed' ? "bg-green-100 text-green-700" :
                message.action_status === 'in_progress' ? "bg-yellow-100 text-yellow-700" :
                "bg-blue-100 text-blue-700"
              )}>
                <CheckSquare className="h-3 w-3 mr-1" />
                {message.action_status === 'completed'
                  ? t('messaging.action.statusDone', 'Done')
                  : message.action_status === 'in_progress'
                    ? t('messaging.action.statusInProgress', 'In Progress')
                    : t('messaging.action.statusPending', 'Action')}
              </Badge>
            )}
          </div>
          {/* Attachments - inside bubble */}
          {message.has_attachments && message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {message.attachments.map((attachment) => (
                <AttachmentDisplay
                  key={attachment.id}
                  attachment={attachment}
                  channelId={message.channel_id}
                  compact={message.attachments.length > 2}
                  isOwn={isOwnMessage}
                />
              ))}
            </div>
          )}

          {/* Tags - inside bubble */}
          {message.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {message.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Thread Preview - inside bubble */}
          {message.reply_count > 0 && (
            <button
              onClick={onOpenThread}
              className={cn(
                "flex items-center gap-2 mt-2 text-sm hover:underline",
                onBlue ? "text-white/80" : "text-blue-500"
              )}
            >
              <MessageSquare className="h-4 w-4" />
              {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
            </button>
          )}

          {/* Reactions — iMessage-style pills on the bubble's top inner corner */}
          {hasReactions && (
            <ReactionBadges
              reactions={message.reactions}
              currentUserId={currentUserId}
              side={onRight ? 'right' : 'left'}
              onToggle={onReaction}
              nameOf={nameOf}
            />
          )}
        </div>
        {isOwnMessage && (
          <SeenStatus
            message={message}
            isDM={isDM}
            isLatestOwn={isLatestOwn}
            visible={showActions}
            align={onRight ? 'right' : 'left'}
          />
        )}
        </div>

      </div>

      {/* Action Buttons */}
      {(showActions || showEmojiPicker || dropdownOpen) && (
        <div data-testid="message-actions" className={cn(
          "absolute flex items-center gap-0.5 bg-white dark:bg-slate-900 border rounded-full shadow-md p-0.5 z-10",
          // Reaction pills own the bubble's top edge, so the toolbar drops to the
          // bottom edge on those messages instead of covering them.
          hasReactions ? "-bottom-3" : "-top-3",
          // Sit next to the bubble on its inner side (member=left bubble → actions
          // just right of the avatar; staff=right bubble → actions at the right edge).
          onRight ? "right-1" : "left-10"
        )}>
          <button
            type="button"
            className={cn(
              "h-7 w-7 p-0 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors",
              (message.priority === 'high' || message.priority === 'urgent') && "text-orange-500"
            )}
            onClick={(e) => {
              e.stopPropagation();
              // Quick flag toggle (high ⇄ normal) without digging into the menu.
              onSetPriority(message.priority === 'high' || message.priority === 'urgent' ? 'normal' : 'high');
            }}
            title={message.priority === 'high' || message.priority === 'urgent' ? 'Clear flag' : 'Flag (high priority)'}
          >
            <Flag className={cn('h-4 w-4', (message.priority === 'high' || message.priority === 'urgent') ? 'text-orange-500' : 'text-gray-600 dark:text-gray-400')}
                  fill={(message.priority === 'high' || message.priority === 'urgent') ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className="h-7 w-7 p-0 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onToggleEmojiPicker();
            }}
            title="Add reaction"
          >
            <Smile className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            type="button"
            className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onReply();
            }}
            title="Reply"
          >
            <Reply className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          {isOwnMessage && (
            <button
              type="button"
              className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Edit"
            >
              <Pencil className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <button
            type="button"
            className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onPin();
            }}
            title={message.is_pinned ? "Unpin" : "Pin"}
          >
            <Pin className={cn('h-4 w-4', message.is_pinned ? 'text-primary' : 'text-gray-600 dark:text-gray-400')} />
          </button>

          {/* More Actions Dropdown */}
          <DropdownMenu open={dropdownOpen} onOpenChange={(open) => {
            setDropdownOpen(open);
            if (open) fetchActionTemplates();
          }}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="More actions"
              >
                <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuLabel>{t('messaging.message.actions', 'Message Actions')}</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Priority submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Flag className="mr-2 h-4 w-4" />
                  <span>{t('messaging.message.setPriority', 'Set Priority')}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => onSetPriority('normal')}>
                    <span className="mr-2">○</span> Normal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSetPriority('high')}>
                    <Flag className="mr-2 h-4 w-4 text-orange-500" /> High
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSetPriority('urgent')}>
                    <AlertTriangle className="mr-2 h-4 w-4 text-red-500" /> Urgent
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              {/* Mark Read/Unread */}
              {message.is_read === false ? (
                <DropdownMenuItem onClick={onMarkRead}>
                  <MailOpen className="mr-2 h-4 w-4" />
                  Mark as Read
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={onMarkUnread}>
                  <Mail className="mr-2 h-4 w-4" />
                  Mark as Unread
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Create Task / Open Task */}
              {!message.linked_task_id ? (
                <DropdownMenuItem onClick={onCreateTask}>
                  <ClipboardList className="mr-2 h-4 w-4" />
                  {t('messaging.task.createMenu', 'Create Task')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => openTaskFromMessaging(message.linked_task_id as number)}>
                  <ClipboardList className="mr-2 h-4 w-4" />
                  {t('messaging.task.badgeOpen', 'Open Task #{{id}}', { id: message.linked_task_id })}
                </DropdownMenuItem>
              )}

              {/* Advance an action-required message */}
              {message.is_action_required && (
                <>
                  {(!message.action_status || message.action_status === 'pending') && (
                    <DropdownMenuItem onClick={() => onSetActionStatus('in_progress')}>
                      <PlayCircle className="mr-2 h-4 w-4" />
                      {t('messaging.action.markInProgress', 'Mark in progress')}
                    </DropdownMenuItem>
                  )}
                  {message.action_status !== 'completed' && (
                    <DropdownMenuItem onClick={() => onSetActionStatus('completed')}>
                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
                      {t('messaging.action.markDone', 'Mark done')}
                    </DropdownMenuItem>
                  )}
                  {(message.action_status === 'completed' || message.action_status === 'in_progress') && (
                    <DropdownMenuItem onClick={() => onSetActionStatus('pending')}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {t('messaging.action.reopen', 'Reopen')}
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {/* Workflow Actions */}
              {(actionTemplates.length > 0 || loadingActions) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Zap className="mr-2 h-4 w-4 text-purple-500" />
                      <span>Workflow Actions</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-56">
                      {loadingActions ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Loading actions...
                        </div>
                      ) : actionTemplates.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No actions available
                        </div>
                      ) : (
                        actionTemplates.map((template) => {
                          const IconComponent = getActionIcon(template.action_icon);
                          return (
                            <DropdownMenuItem
                              key={template.id}
                              onClick={() => {
                                if (template.require_confirmation) {
                                  const confirmed = window.confirm(
                                    template.confirmation_message || `Are you sure you want to ${template.action_label}?`
                                  );
                                  if (!confirmed) return;
                                }
                                onExecuteAction(template.action_code);
                              }}
                              className="flex flex-col items-start gap-0.5"
                            >
                              <div className="flex items-center">
                                <IconComponent className="mr-2 h-4 w-4 text-purple-500" />
                                <span>{template.action_label}</span>
                              </div>
                              {template.action_description && (
                                <span className="text-xs text-muted-foreground ml-6 line-clamp-1">
                                  {template.action_description}
                                </span>
                              )}
                            </DropdownMenuItem>
                          );
                        })
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              {/* Delete (own messages only) */}
              {isOwnMessage && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onDelete} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Message
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className={cn(
          "absolute right-0 bg-white dark:bg-slate-900 border rounded shadow-lg p-2 z-20",
          hasReactions ? "top-full mt-4" : "top-10"
        )}>
          <div className="flex gap-1">
            {COMMON_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={(e) => {
                  e.stopPropagation();
                  onReaction(emoji, hasReacted(emoji));
                }}
                className={cn(
                  'text-xl hover:bg-slate-100 dark:hover:bg-slate-800 p-1 rounded',
                  hasReacted(emoji) && 'bg-primary/10'
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageThread;
